/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : PowerTrainClass.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author Abdul Rahim M - abdul@au79consulting.com
 * @date 1-07-2024
 * @description Component for handling the PowerTrain Class.
 */
import { Box, DialogContentText, IconButton, Stack } from "@mui/material";

import deleteIcon from "../../assets/delete_icon.png";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import customConsole from "../../config/customConsole";

import { HandleApiActions } from "../../redux/actions/actionHandler";
import profileActions from "../../redux/actions/profile";
import adminSettingsAPI from "../../redux/actions/adminSetttingAPI";

import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";
import { ChipsTextField } from "../../components";
import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomSearchbarWithApiV2 from "../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import CustomDataGridV2 from "../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import CustomSelectV2 from "../../components/customComponentsV2/common/select/CustomSelectV2";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import * as XLSX from "xlsx";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import PropTypes from "prop-types";

const getPowerTrainList = (resp) => {
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.response?.data)) return resp.response.data;
  return [];
};

function PowerTrainClass({ open, handleClick }) {
  // Add resize detection state
  const [tableKey, setTableKey] = useState(0);
  const [totalPowerTrainClassCount, setTotalPowerTrainClassCount] = useState(0);

  // Force table re-render when component becomes active
  useEffect(() => {
    if (open === "powertrain") {
      setTableKey((prev) => prev + 1);
    }
  }, [open]);

  // Add window resize listener
  useEffect(() => {
    const handleResize = () => {
      setTableKey((prev) => prev + 1);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const COLUMNS = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        flex: 0.3,
        minWidth: 60,
      },
      {
        field: "name",
        headerName: "Name",
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: "abbreviation",
        headerName: "Abbreviation",
        flex: 0.5,
        minWidth: 80,
      },
      {
        field: "description",
        headerName: "Description",
        flex: 0.9,
        minWidth: 140,
      },
      {
        field: "configuration",
        headerName: "Config",
        flex: 0.8,
        minWidth: 110,
        renderCell: (params) => {
          const optionList = params?.row?.configuration?.map((item) => ({
            label: item,
            value: item,
          }));
          return (
            <CustomSelectV2
              label={" "}
              required={false}
              optionsList={optionList}
              selectedValue={params?.row?.configuration?.[0] || "No data"}
            />
          );
        },
      },
      {
        field: "created_at",
        headerName: "Created At",
        flex: 0.6,
        minWidth: 100,
      },
      {
        field: "updated_at",
        headerName: "Updated At",
        flex: 0.6,
        minWidth: 100,
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.4,
        minWidth: 90,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              size="small"
              sx={{ color: "#000000", p: 0.5 }}
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(params);
              }}
            >
              <BorderColorIcon fontSize="inherit" sx={{ fontSize: 19 }} />
            </IconButton>
            <IconButton
              size="small"
              sx={{ p: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(params);
              }}
            >
              <img
                src={deleteIcon}
                alt="delete"
                style={{ width: 24, height: 24 }}
              />
            </IconButton>
          </Box>
        ),
      },
    ],
    [],
  );

  //1.Redux

  const dispatch = useDispatch();
  const powerTrainClassDetails = useSelector(
    (state) => state.powerTrainClassReducer,
  );
  const timeZoneDetails = useSelector((state) => state.profileReducer);

  //2.React useState
  const [page, setPage] = React.useState(1); // Changed to 1-based pagination like VehicleSystems
  const [tableRowsData, setTableRowsData] = useState([]);
  const [rowsPerPage] = React.useState(25);
  const [getPowerTrainClass, setGetPowerTrainClass] = React.useState([]);
  const [openAddPowerDialog, setOpenAddPowerDialog] = React.useState(false);
  const [openEditPowerDialog, setOpenEditPowerDialog] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [openDeletePowerDialog, setOpenDeletePowerDialog] =
    React.useState(false);
  const [searchQuery] = useState("");

  //3.React useEffect

  useEffect(() => {
    console.log("getPowerTrainClass?????", getPowerTrainClass);
    let filteredUsersList = getPowerTrainClass?.map((data, index) => {
      return {
        ...data,
        sno: (page - 1) * rowsPerPage + index + 1, // Calculate serial number based on pagination
        id: data?.id,
        abbreviation: data?.abbreviation,
        name: data?.name,
        configuration: data?.other_fields?.configurations,
        description: data?.other_fields?.description,
        created_at: convertUtcToTimeZone(
          data?.created_at,
          timeZoneDetails?.profile?.profile?.region,
        ),
        updated_at: convertUtcToTimeZone(
          data?.updated_at,
          timeZoneDetails?.profile?.profile?.region,
        ),
      };
    });

    console.log("filteredUsersList", filteredUsersList);

    setTableRowsData(filteredUsersList || []); // Ensure it's always an array
  }, [
    getPowerTrainClass,
    page,
    rowsPerPage,
    timeZoneDetails?.profile?.profile?.region,
  ]);

  const filteredRows = useMemo(() => {
    if (!searchQuery) return tableRowsData;
    return tableRowsData.filter((row) =>
      COLUMNS.some((column) =>
        row[column.field]
          ?.toString()
          .toLowerCase()
          .includes(searchQuery.toLowerCase()),
      ),
    );
  }, [searchQuery, tableRowsData, COLUMNS]);

  // API call function with pagination support similar to VehicleSystems
  const getPowerTrainClassDetails = useCallback(
    async (pageOverride) => {
      const pageToUse = pageOverride ?? page;
      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_powertrain_class,
          show_toast: false,
          params: {
            page_number: pageToUse,
            page_size: rowsPerPage,
          },
        }),
      );

      if (resp?.error) {
        setGetPowerTrainClass([]);
        setTableRowsData([]);
        setTotalPowerTrainClassCount(0);
        return { list: [], totalCount: 0 };
      }

      const list = getPowerTrainList(resp);

      const totalCount =
        resp?.total_count ??
        resp?.totalItems ??
        resp?.total ??
        (Array.isArray(list) ? list.length : 0);

      setGetPowerTrainClass(list);
      setTotalPowerTrainClassCount(totalCount);

      return { list, totalCount };
    },
    [dispatch, page, rowsPerPage],
  );

  useEffect(() => {
    getPowerTrainClassDetails();
  }, [page, rowsPerPage, getPowerTrainClassDetails]);

  useEffect(() => {
    dispatch(HandleApiActions({ ...profileActions.get_coutry_regions }));
  }, [dispatch]);

  useEffect(() => {
    const region = timeZoneDetails?.profile?.profile?.region;
    if (region?.length > 0) {
      customConsole("timeZoneDetails?.data?.profile?.region");
      customConsole(region);
    }
  }, [timeZoneDetails]);

  useEffect(() => {
    customConsole("Power Train Details");
    customConsole(powerTrainClassDetails);
  }, [powerTrainClassDetails]);

  //4.Functions
  // Toggles the state of the dialog.
  const toggleDialog = (newOpen) => () => {
    setOpenAddPowerDialog(newOpen);
  };
  //Function To Add Button
  const handleAddPowerTrainClick = () => {
    toggleDialog(true)();
  };

  // Function to handle Edit button click
  const handleEditClick = (params) => {
    setSelectedRow(params);
    customConsole("Edit clicked for row:", params);
    setOpenEditPowerDialog(true);
  };

  // Function to handle Delete button click
  const handleDeleteClick = (params) => {
    setSelectedRow(params);
    customConsole("Delete clicked for row:", params);
    setOpenDeletePowerDialog(true);
  };

  // Function to handle export to Excel
  const handleExportToExcel = () => {
    if (!filteredRows || filteredRows?.length === 0) {
      console.warn("No data to export");
      return;
    }

    // Prepare data for export - map to clean format without internal fields
    const exportData = filteredRows?.map((row) => ({
      "S.No": row?.sno,
      Name: row?.name,
      Abbreviation: row?.abbreviation,
      Description: row?.description,
      Configuration: Array.isArray(row?.configuration)
        ? row?.configuration.join(", ")
        : row?.configuration || "",
      Created: row?.created_at,
      Updated: row?.updated_at,
    }));

    // Create a new workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Powertrain Classes");

    // Generate and download Excel file
    XLSX.writeFile(wb, "Powertrain_Classes.xlsx");
  };

  const breadcrumbItems = [
    { label: "Settings" },
    { label: "Feature Portfolio" },
    { label: "Powertrain Class" },
  ];

  return (
    <Box
      sx={{
        margin: 0,
        padding: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F5F5F5",
      }}
    >
      {/* Breadcrumbs and description */}
      <Box
        sx={{
          padding: { xs: "8px 8px 0px 8px", md: "12px 12px 0px 12px" },
          flexShrink: 0,
        }}
      >
        <CommonBreadcrumbs items={breadcrumbItems} />
      </Box>

      {/* Header with Search and Add Button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: { xs: "0 16px 12px 16px", md: "0 24px 16px 24px" },
          flexShrink: 0,
          mt: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box>
            <CustomSearchbarWithApiV2
              params={{
                page_number: page,
                page_size: rowsPerPage,
              }}
              apiAction={adminSettingsAPI.get_powertrain_class}
              searchKey={"name"}
              getDataOnSearch={(data, resp) => {
                console.log("Search Data:", resp);
                if (resp?.error) {
                  setGetPowerTrainClass([]);
                  setTableRowsData([]);
                  setTotalPowerTrainClassCount(0);
                  return;
                }

                const searchedList = Array.isArray(data) ? data : [];
                setGetPowerTrainClass(searchedList);
                setTotalPowerTrainClassCount(
                  resp?.total_count ??
                    resp?.totalItems ??
                    resp?.total ??
                    searchedList.length,
                );
              }}
              placeholder={"Search"}
            />
          </Box>
          <Box>
            <AddButtonV2 handleOnClick={handleAddPowerTrainClick} />
          </Box>
        </Box>
        <Box>
          <CustomButtonV2
            startIcon={<FileDownloadOutlinedIcon />}
            type="submit"
            size="small"
            btnName="Export"
            handleOnClick={handleExportToExcel}
          />
        </Box>
      </Box>

      {/* DataGrid Container */}
      <Box
        sx={{
          height: "76vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          padding: { xs: "0 8px", md: "0 16px" },
          mt: -1,
        }}
        key={`powertrain-table-${tableKey}`}
      >
        <CustomDataGridV2
          rows={filteredRows}
          columns={COLUMNS}
          isMultiLine={true}
          onRowClick={handleEditClick}
          showPagination={true}
          page={page}
          rowsPerPage={rowsPerPage}
          handlePageChange={(newPage) => setPage(newPage)}
          totalItems={totalPowerTrainClassCount}
        />
      </Box>

      <AddPowerTrainClass
        open={openAddPowerDialog}
        toggleDialog={toggleDialog}
        setPage={setPage}
        refreshPowerTrainClass={getPowerTrainClassDetails}
      />
      <EditPowerTrainClass
        open={openEditPowerDialog}
        setOpenEditPowerDialog={setOpenEditPowerDialog}
        selectedRow={selectedRow}
        page={page}
        refreshPowerTrainClass={getPowerTrainClassDetails}
      />
      <DeletePowerTrainClass
        openDeletePowerDialog={openDeletePowerDialog}
        setOpenDeletePowerDialog={setOpenDeletePowerDialog}
        selectedRow={selectedRow}
        setPage={setPage}
        page={page}
        rowsPerPage={rowsPerPage}
        refreshPowerTrainClass={getPowerTrainClassDetails}
      />
    </Box>
  );
}

export default PowerTrainClass;

const powerTrainRowShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  name: PropTypes.string,
  abbreviation: PropTypes.string,
  configuration: PropTypes.array,
  other_fields: PropTypes.shape({
    description: PropTypes.string,
    configurations: PropTypes.array,
  }),
});

const selectedPowerTrainShape = PropTypes.shape({
  row: powerTrainRowShape,
});

PowerTrainClass.propTypes = {
  open: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleClick: PropTypes.func,
};

// Styling for the Users component
const styles = {
  container: {
    width: "100%",
    overflow: "auto",
    margin: 0,
    padding: 0,
    boxShadow: "none",
    border: "none",
  },
  tblFun: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  list1: {
    background: "transparent",
    margin: 0,
    padding: 0,
  },
};

const AddPowerTrainClass = ({
  open,
  toggleDialog,
  setPage,
  refreshPowerTrainClass,
}) => {
  //1.Redux
  const dispatch = useDispatch();

  //2.React useState
  const [name, setName] = useState("");
  const [abbreviation, setAbbreviation] = useState("");
  const [description, setDescription] = useState("");
  const [configuration, setConfiguration] = useState([]);

  //3.Functions
  const handleClose = () => {
    toggleDialog(false)();
    setName("");
    setAbbreviation("");
    setDescription("");
  };

  const handleClickAdd = async () => {
    try {
      const reqObj = {
        current_time: new Date(),
        abbreviation: abbreviation,
        name: name,
        ...(configuration ? { configurations: configuration } : {}),
      };

      if (description?.trim()) {
        reqObj.description = description;
      }

      await dispatch(
        HandleApiActions({
          ...adminSettingsAPI?.add_powertrain_class,
          show_toast: true,
          params: reqObj,
        }),
      );
      toggleDialog(false)();

      setPage(1);
      await refreshPowerTrainClass(1);
      setName("");
      setDescription("");
      setAbbreviation("");
      setConfiguration([]);
    } catch (error) {
      console.error("Error adding powertrain class:", error);
    }
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={handleClose}
      title={"Add Powertrain Class"}
      successButtonName={"Add"}
      rejectButtonName={"Close"}
      onClickSuccess={handleClickAdd}
      actionButtonStyle={"continuous"}
      onClickReject={handleClose}
      onDisabled={
        !(
          name?.length > 2 &&
          name?.trim() !== "" &&
          abbreviation?.length > 2 &&
          abbreviation?.trim() !== ""
        )
      }
    >
      <Stack spacing={2} sx={{ width: "350px" }}>
        <CustomFormTextFieldV2
          required={true}
          label="Name"
          value={name}
          setValue={(val) => {
            if (val.length <= 50) {
              setName(val);
            }
          }}
          maxLength={50}
        />
        <CustomFormTextFieldV2
          required
          size="small"
          label="Abbreviation"
          value={abbreviation}
          setValue={(val) => {
            if (val.length <= 35) {
              setAbbreviation(val);
            }
          }}
          maxLength={35}
        />
        <CustomFormTextFieldV2

          label="Description"
          value={description}
          setValue={(val) => {
            if (val.length <= 250) {
              setDescription(val);
            }
          }}
          fldType={"notext"}
          maxLength={250}
        />
        <ChipsTextField
          size={"small"}
          label={"Configurations"}
          variant={"outlined"}
          chips={configuration}
          setChips={(val) => {
            if (val.length <= 35) {
              setConfiguration(val);
            }
          }}
          maxLength={35}
        />
      </Stack>
    </CustomDialogBoxV2>
  );
};

AddPowerTrainClass.propTypes = {
  open: PropTypes.bool,
  toggleDialog: PropTypes.func,
  setPage: PropTypes.func,
  refreshPowerTrainClass: PropTypes.func,
};

const EditPowerTrainClass = ({
  open,
  setOpenEditPowerDialog,
  selectedRow,
  page,
  refreshPowerTrainClass,
}) => {
  //1.Redux
  const dispatch = useDispatch();

  //2.React useState
  const [editName, setEditName] = useState("");
  const [editAbbreviation, setEditAbbreviation] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editConfiguration, setEditConfiguration] = useState([]);
  const [descriptionError, setDescriptionError] = useState("");
  const [originalDescription, setOriginalDescription] = useState("");
  const [originalConfiguration, setOriginalConfiguration] = useState([]);

  ///3.React useEffect
  React.useEffect(() => {
    console.log("selectedRow?.row", selectedRow?.row);
    if (selectedRow) {
      setEditName(selectedRow?.row?.name || "");
      setEditAbbreviation(selectedRow?.row?.abbreviation || "");
      setEditDescription(selectedRow?.row?.other_fields?.description || "");
      setEditConfiguration(selectedRow?.row?.configuration || []);
      setOriginalDescription(selectedRow?.row?.other_fields?.description || "");
      setOriginalConfiguration(selectedRow?.row?.configuration || []);
    }
  }, [selectedRow]);

  const isConfigEqual = (a, b) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    const compareValues = (x, y) => String(x).localeCompare(String(y));
    const sa = [...a].sort(compareValues);
    const sb = [...b].sort(compareValues);
    return sa.every((v, i) => v === sb[i]);
  };

  // Optional field validation flags
  const descChanged = editDescription !== originalDescription;
  const configChanged = !isConfigEqual(
    editConfiguration,
    originalConfiguration,
  );
  const descValid = editDescription.trim().length >= 3;
  const configValid =
    Array.isArray(editConfiguration) && editConfiguration.length > 0;

  // Enable only if: description changed is valid (when changed), and at least one valid change exists
  const canUpdate =
    (descChanged ? descValid : true) &&
    ((descChanged && descValid) || (configChanged && configValid));

  //4.Functions
  const handleClose = () => {
    setOpenEditPowerDialog(false);
  };

  const handleClickEdit = async () => {
    try {
      const reqObj = {
        current_time: new Date().toISOString(),
        id: selectedRow?.row?.id,
      };
      // Include only changed optional fields
      if (descChanged && descValid) {
        reqObj.description = editDescription;
      }
      if (configChanged) {
        reqObj.configurations = editConfiguration;
      }
      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.update_powertrain_class,
          params: reqObj,
          show_toast: true,
        }),
      );
      if (!resp?.error) {
        setOpenEditPowerDialog(false);
      }

      await refreshPowerTrainClass(page);
    } catch (error) {
      console.error("Error updating powertrain class:", error);
    }
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={handleClose}
      title={"Update Powertrain Class"}
      successButtonName={"Update"}
      rejectButtonName={"Close"}
      onClickSuccess={handleClickEdit}
      onDisabled={!canUpdate}
      actionButtonStyle={"continuous"}
      onClickReject={handleClose}
    >
      <Stack spacing={2} sx={{ width: "350px" }}>
        <CustomFormTextFieldV2
          label={"Name"}
          value={editName}
          setValue={(val) => {
            if (val.length <= 50) {
              setEditName(val);
            }
          }}
          disabled={true}
          required={true}
          maxLength={50}
        />
        <CustomFormTextFieldV2
          label={"Abbreviation"}
          value={editAbbreviation}
          setValue={(val) => {
            if (val.length <= 35) {
              setEditAbbreviation(val);
            }
          }}
          disabled={true}
          required={true}
          maxLength={35}
        />
        <CustomFormTextFieldV2
          label={"Description"}
          value={editDescription}
          setValue={(val) => {
            if (val.length <= 250) {
              setEditDescription(val);
            }
          }}
          setError={setDescriptionError}
          error={descriptionError !== ""}
          maxLength={250}
        />
        <ChipsTextField
          size={"small"}
          label={"Configurations"}
          chips={editConfiguration}
          setChips={setEditConfiguration}
          txtFldStyle={styles.chipsTextField}
        />
      </Stack>
    </CustomDialogBoxV2>
  );
};

EditPowerTrainClass.propTypes = {
  open: PropTypes.bool,
  setOpenEditPowerDialog: PropTypes.func,
  selectedRow: selectedPowerTrainShape,
  page: PropTypes.number,
  refreshPowerTrainClass: PropTypes.func,
};

const DeletePowerTrainClass = ({
  openDeletePowerDialog,
  setOpenDeletePowerDialog,
  selectedRow,
  setPage,
  page,
  rowsPerPage,
  refreshPowerTrainClass,
}) => {
  //Redux
  const dispatch = useDispatch();

  const handleDelete = async () => {
    try {
      const reqObj = {
        current_time: new Date(),
        id: selectedRow?.row?.id,
      };
      await dispatch(
        HandleApiActions({
          ...adminSettingsAPI?.delete_powertrain_class,
          show_toast: true,
          params: reqObj,
        }),
      );
      setOpenDeletePowerDialog(false);

      const refreshResult = await refreshPowerTrainClass(page);
      const totalCount = refreshResult?.totalCount ?? 0;
      if (page > 1 && totalCount <= (page - 1) * rowsPerPage) {
        const newPage = page - 1;
        setPage(newPage);
        await refreshPowerTrainClass(newPage);
      }
    } catch (error) {
      console.error("Error deleting powertrain class:", error);
    }
  };
  return (
    <CustomDialogBoxV2
      open={openDeletePowerDialog}
      onClose={() => setOpenDeletePowerDialog(false)}
      title={"Delete Powertrain Class"}
      successButtonName={"Ok"}
      rejectButtonName={"Cancel"}
      onClickSuccess={handleDelete}
      onClickReject={() => setOpenDeletePowerDialog(false)}
      actionButtonStyle={"continuous"}
    >
      <Box
        sx={{
          width: "350px",
          height: "50px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <DialogContentText>Are you sure you want to delete?</DialogContentText>
      </Box>
    </CustomDialogBoxV2>
  );
};

DeletePowerTrainClass.propTypes = {
  openDeletePowerDialog: PropTypes.bool,
  setOpenDeletePowerDialog: PropTypes.func,
  selectedRow: selectedPowerTrainShape,
  setPage: PropTypes.func,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  refreshPowerTrainClass: PropTypes.func,
};
